// src/admin/EarlyAttendanceStats.js
import React, { useState, useContext } from 'react';
import { useOutletContext } from 'react-router-dom';
import { StudentContext } from '../StudentContext'; // If classMap is needed for filename
import * as XLSX from 'xlsx';

const EarlyAttendanceStats = () => {
  const { allStudentsForOutlet, selectedClassForOutlet } = useOutletContext();
  const { classMap } = useContext(StudentContext); // For filename

  const allStudents = allStudentsForOutlet || [];

  const [earlyAttendanceDateRange, setEarlyAttendanceDateRange] = useState({
    startDate: '',
    endDate: ''
  });

  const handleEarlyAttendanceDateChange = (e) => {
    setEarlyAttendanceDateRange({
      ...earlyAttendanceDateRange,
      [e.target.name]: e.target.value
    });
  };

  const calculateEarlyAttendance = (student) => {
    let sundayEarlyCount = 0;
    let thursdayCount = 0;
    const startDate = earlyAttendanceDateRange.startDate ? new Date(earlyAttendanceDateRange.startDate) : null;
    const endDate = earlyAttendanceDateRange.endDate ? new Date(earlyAttendanceDateRange.endDate) : null;
    const dayOfWeek = ["Chúa nhật", "Thứ hai", "Thứ ba", "Thứ tư", "Thứ năm", "Thứ sáu", "Thứ bảy"];

    Object.keys(student.attendance || {}).forEach(date => {
      const attendanceRecord = student.attendance[date];
      if (!attendanceRecord || !attendanceRecord.includes('Có lúc')) return;
      const parts = date.split(' - ');
      if (parts.length < 2) return; // Invalid date format
      const dayName = parts[0].trim();
      const dateStr = parts[1].trim();
      
      const dateParts = dateStr.split('/');
      if (dateParts.length < 3) return; // Invalid date string
      const day = parseInt(dateParts[0], 10);
      const month = parseInt(dateParts[1], 10) -1; // JS months are 0-indexed
      const year = parseInt(dateParts[2], 10);
      const dateObj = new Date(year, month, day);

      if (isNaN(dateObj.getTime())) return;
      if (startDate && dateObj < startDate) return;
      if (endDate && dateObj > endDate) return;

      const isSunday = dayName === dayOfWeek[0];
      const isThursday = dayName === dayOfWeek[4];
      
      const timeStrMatch = attendanceRecord.match(/(\d{1,2}:\d{2})/);
      if (!timeStrMatch) return;
      const timeStr = timeStrMatch[0];
      const [hours, minutes] = timeStr.split(':').map(Number);
      const isEarly = hours < 7 || (hours === 7 && minutes <= 40);

      if (isEarly && isSunday) sundayEarlyCount++;
      if (isThursday) thursdayCount++;
    });
    return { sundayEarlyCount, thursdayCount };
  };

  const handleExportEarlyAttendanceToExcel = () => {
    if (allStudents.length === 0) {
        alert("Không có dữ liệu học sinh để xuất.");
        return;
    }
    const header = ['ID', 'Tên Thánh', 'Họ và Tên', 'Số ngày Chúa Nhật đi lễ sớm (Trước 7:40)', 'Số ngày Đi lễ Thứ năm'];
    const data = allStudents.map(student => {
      const { sundayEarlyCount, thursdayCount } = calculateEarlyAttendance(student);
      return [
        student.id,
        student.holyName || '',
        student.fullName || '',
        sundayEarlyCount,
        thursdayCount
      ];
    });
    const wb = XLSX.utils.book_new();
    const wsData = [header, ...data];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, 'Thống kê đi lễ sớm');
    
    const className = (selectedClassForOutlet === 'all' || !classMap[selectedClassForOutlet]) 
                      ? 'tat_ca_lop' 
                      : classMap[selectedClassForOutlet].replace(/\s+/g, '_'); // Sanitize class name
    const filename = `ThongKe_DiLeSom_${className}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-4">Thống kê thiếu nhi đi lễ sớm (Trước 7:40 sáng Chúa Nhật)</h2>
      <div className="flex flex-wrap justify-center items-center mb-6 gap-4">
        <button
          onClick={handleExportEarlyAttendanceToExcel}
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
        >
          Tải về file Excel
        </button>
      </div>
      <div className="date-range-picker mb-6 text-center space-y-4 md:space-y-0 md:space-x-4">
        <label className="inline-block">
          Từ ngày:
          <input
            type="date"
            name="startDate"
            value={earlyAttendanceDateRange.startDate}
            onChange={handleEarlyAttendanceDateChange}
            className="ml-2 p-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
          />
        </label>
        <label className="inline-block">
          Đến ngày:
          <input
            type="date"
            name="endDate"
            value={earlyAttendanceDateRange.endDate}
            onChange={handleEarlyAttendanceDateChange}
            className="ml-2 p-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
          />
        </label>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full w-full border-collapse">
          <thead>
            <tr className="bg-gray-200">
              <th className="border p-2 text-left">ID</th>
              <th className="border p-2 text-left">Tên Thánh</th>
              <th className="border p-2 text-left">Họ và Tên</th>
              <th className="border p-2 text-center">Chúa Nhật đi lễ sớm</th>
              <th className="border p-2 text-center">Đi lễ Thứ năm</th>
            </tr>
          </thead>
          <tbody>
            {allStudents.map((student, index) => {
              const { sundayEarlyCount, thursdayCount } = calculateEarlyAttendance(student);
              return (
                <tr key={student.id} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                  <td className="border p-2">{student.id}</td>
                  <td className="border p-2">{student.holyName || ''}</td>
                  <td className="border p-2">{student.fullName || ''}</td>
                  <td className="border p-2 text-center">{sundayEarlyCount}</td>
                  <td className="border p-2 text-center">{thursdayCount}</td>
                </tr>
              );
            })}
            {allStudents.length === 0 && (
               <tr>
                <td colSpan="5" className="text-center p-4 border-t">
                  Không có học sinh nào trong lớp được chọn, hoặc không có dữ liệu học sinh.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EarlyAttendanceStats;